import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldAlert, Activity, Eye, GitBranch, MapPin, ScanLine } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuthStore } from '@/app/store/auth'
import { TimelineTab } from '../components/TimelineTab'
import { ObjectViewTab } from '../components/ObjectViewTab'
import { CrossObjectTab } from '../components/CrossObjectTab'
import { VaultLocationTab } from '../components/VaultLocationTab'
import { ScanResultsTab } from '../components/ScanResultsTab'

interface AnalyticsSummary {
    totalVersions: number
    cleanCount: number
    anomalyCount: number
    highSeverityCount: number
    threatCount: number
    informationalCount: number
    notScannedCount: number
    affectedObjects: number
    highSevObjects: number
    highSevVaults: number
}

export function AnalyticsPage() {
    const { t } = useTranslation()
    const accessToken = useAuthStore((s) => s.accessToken)
    const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        async function fetchSummary() {
            try {
                const res = await fetch('/api/analytics/summary', {
                    headers: { Authorization: `Bearer ${accessToken}` },
                })
                const json = await res.json()
                if (json.success) {
                    setSummary(json.data)
                }
            } catch {
                // silently fail for now
            } finally {
                setLoading(false)
            }
        }
        fetchSummary()
    }, [accessToken])

    const cards = summary
        ? [
              { label: 'Total Versions', value: summary.totalVersions },
              { label: 'Clean', value: summary.cleanCount },
              { label: 'Anomalies', value: summary.anomalyCount },
              { label: 'High Severity', value: summary.highSeverityCount },
              { label: 'Threats', value: summary.threatCount },
              { label: 'Informational', value: summary.informationalCount },
              { label: 'Not Scanned', value: summary.notScannedCount },
              { label: 'Affected Objects', value: summary.affectedObjects },
              { label: 'High-Sev Objects', value: summary.highSevObjects },
              { label: 'High-Sev Vaults', value: summary.highSevVaults },
          ]
        : []

    return (
        <div className="flex flex-col gap-6 p-6">
            {/* Page header */}
            <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <ShieldAlert className="h-6 w-6" />
                    {t('navigation.analyticsAnomaly', 'Analytics & Anomaly')}
                </h1>
                <p className="text-muted-foreground mt-1">
                    Anomaly detection, analytics scan results, and cross-object pattern analysis across all vaults and objects.
                </p>
            </div>

            {/* Summary cards */}
            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {Array.from({ length: 10 }).map((_, i) => (
                        <Card key={i}>
                            <CardContent className="p-4">
                                <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                                <div className="h-8 w-16 bg-muted animate-pulse rounded mt-2" />
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {cards.map((card) => (
                        <Card key={card.label}>
                            <CardContent className="p-4">
                                <p className="text-xs font-medium text-muted-foreground">{card.label}</p>
                                <p className="text-2xl font-bold mt-1">{card.value.toLocaleString()}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="timeline" className="w-full">
                <TabsList>
                    <TabsTrigger value="timeline" className="flex items-center gap-1.5">
                        <Activity className="h-4 w-4" />
                        Timeline
                    </TabsTrigger>
                    <TabsTrigger value="object-view" className="flex items-center gap-1.5">
                        <Eye className="h-4 w-4" />
                        Object View
                    </TabsTrigger>
                    <TabsTrigger value="cross-object" className="flex items-center gap-1.5">
                        <GitBranch className="h-4 w-4" />
                        Cross-Object Patterns
                    </TabsTrigger>
                    <TabsTrigger value="vault-location" className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4" />
                        Vault & Location
                    </TabsTrigger>
                    <TabsTrigger value="analytics-scans" className="flex items-center gap-1.5">
                        <ScanLine className="h-4 w-4" />
                        Analytics Scans
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="timeline">
                    <TimelineTab />
                </TabsContent>

                <TabsContent value="object-view">
                    <ObjectViewTab />
                </TabsContent>

                <TabsContent value="cross-object">
                    <CrossObjectTab />
                </TabsContent>

                <TabsContent value="vault-location">
                    <VaultLocationTab />
                </TabsContent>

                <TabsContent value="analytics-scans">
                    <ScanResultsTab />
                </TabsContent>
            </Tabs>
        </div>
    )
}
